import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  coursesApi,
  modulesApi,
  reviewsApi,
  paymentApi,
  enrollmentApi,
} from "../lib";
import { showError, showInfo, showSuccess } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { wishlistApi } from "../lib";
import ShareModal from "../components/ShareModal";
import {
  ArrowLeft,
  Clock,
  Users,
  Star,
  Play,
  BookOpen,
  CheckCircle,
  ExternalLink,
  AlertCircle,
  Code,
  FileText,
  ChevronDown,
  ChevronUp,
  Award,
  Target,
  HelpCircle,
  Lock,
  PlayCircle,
  Globe,
  Zap,
  ThumbsUp,
  Layers,
  Monitor,
  Smartphone,
  Download,
  Share2,
  Heart,
  CheckSquare,
  XCircle,
  RotateCcw,
  Eye,
  Loader2,
  Info,
  BarChart3,
  Trophy,
  Shield,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Lesson {
  id: string;
  title: string;
  type: "video" | "text" | "code" | "quiz" | "assignment" | "mixed";
  duration: number;
  isPreview?: boolean;
  videoUrl?: string;
  paragraphs?: string;
  bullets?: string;
  images?: string;
  links?: string;
  codeBlocks?: string;
}

interface QuizQuestion {
  id: string;
  questionText: string;
  type: "multiple_choice" | "true_false";
  points: number;
  options: Array<{ id: string; optionText: string; isCorrect: boolean }>;
  correctOptionId: string;
  explanation?: string;
}

interface Quiz {
  title: string;
  passingScore: number;
  timeLimit: number;
  questions: QuizQuestion[];
}

interface Module {
  id: string;
  title: string;
  description: string;
  lessons: Lesson[];
  quiz?: Quiz;
  hasQuiz?: boolean;
}

interface Review {
  id: string;
  userName: string;
  avatar: string;
  rating: number;
  date: string;
  comment: string;
  helpful: number;
}

interface Course {
  id: string;
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  difficulty: "beginner" | "intermediate" | "advanced";
  language: string;
  thumbnail: string;
  promoVideo?: string;
  price: number;
  originalPrice: number;
  currency: string;
  discount: number;
  instructorId: string;
  instructorName: string;
  instructorTitle: string;
  instructorAvatar?: string;
  instructorBio?: string;
  instructorRating: number;
  instructorStudents: number;
  instructorCourses: number;
  modules: Module[];
  totalDuration: number;
  totalLessons: number;
  enrolledStudents: number;
  rating: number;
  reviewsCount: number;
  lastUpdated: string;
  published: boolean;
  whatYouWillLearn: string | string[];
  requirements: string | string[];
  prerequisites: string;
  reviews: Review[];
  tags: string[];
  certificate: boolean;
  downloadableResources: number;
  articlesCount: number;
  mobileAccess: boolean;
}

const toArray = (field: string | string[] | undefined): string[] => {
  if (!field) return [];
  if (Array.isArray(field)) return field;
  if (typeof field === "string") {
    return field.split("\n").filter((item) => item.trim());
  }
  return [];
};

const fmtPrice = (price: number, currency: string) =>
  currency === "XAF"
    ? new Intl.NumberFormat("fr-CM", {
        style: "currency",
        currency: "XAF",
        maximumFractionDigits: 0,
      }).format(price)
    : `$${(price / 100).toFixed(2)}`;

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m > 0 ? `${m}m` : ""}`.trim() : `${m}m`;
};

const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const RatingStars = ({
  rating,
  size = "sm",
}: {
  rating: number;
  size?: "sm" | "md" | "lg";
}) => {
  const sz =
    size === "lg" ? "w-5 h-5" : size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`${sz} ${s <= Math.round(rating) ? "text-amber-400 fill-amber-400" : "text-gray-300 fill-gray-300"}`}
        />
      ))}
    </div>
  );
};

const DifficultyBadge = ({ level }: { level: string }) => {
  const map: Record<string, string> = {
    beginner: "bg-emerald-50 text-emerald-700 border-emerald-200",
    intermediate: "bg-blue-50 text-blue-700 border-blue-200",
    advanced: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[level] ?? "bg-gray-100 text-gray-600 border-gray-200"}`}
    >
      {level}
    </span>
  );
};

const LessonIcon = ({ type }: { type: Lesson["type"] }) => {
  const cls = "w-3.5 h-3.5";
  switch (type) {
    case "video":
      return <PlayCircle className={`${cls} text-blue-500`} />;
    case "text":
      return <FileText className={`${cls} text-amber-500`} />;
    case "code":
      return <Code className={`${cls} text-violet-500`} />;
    case "quiz":
      return <HelpCircle className={`${cls} text-rose-500`} />;
    case "assignment":
      return <CheckSquare className={`${cls} text-teal-500`} />;
    default:
      return <BookOpen className={`${cls} text-gray-400`} />;
  }
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CourseDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [openModules, setOpenModules] = useState<Set<string>>(new Set());
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [checkingEnrollment, setCheckingEnrollment] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "overview" | "curriculum" | "instructor" | "reviews"
  >("overview");
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [showAllModules, setShowAllModules] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [newReviewRating, setNewReviewRating] = useState(5);
  const [newReviewComment, setNewReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [helpfulReviews, setHelpfulReviews] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add this effect to check payment status when returning from payment gateway
  useEffect(() => {
    const checkPendingPayment = async () => {
      const pendingEnrollment = sessionStorage.getItem("pendingEnrollment");
      if (pendingEnrollment) {
        const { courseId, transactionId, amount } =
          JSON.parse(pendingEnrollment);

        try {
          // Poll payment status
          const statusRes = await paymentApi.checkPaymentStatus(transactionId);

          if (statusRes.success && statusRes.data?.status === "completed") {
            // Payment successful, check enrollment
            const enrollmentCheck =
              await enrollmentApi.checkEnrollmentStatus(courseId);
            if (enrollmentCheck.isEnrolled) {
              showSuccess("Payment successful! You are now enrolled.");
              setIsEnrolled(true);
              sessionStorage.removeItem("pendingEnrollment");
              navigate(`/course/${courseId}/learn`);
            } else {
              // Try to verify payment
              const verifyRes = await paymentApi.verifyPayment(transactionId);
              if (verifyRes.success && verifyRes.data?.status === "completed") {
                showSuccess("Payment verified! You are now enrolled.");
                setIsEnrolled(true);
                sessionStorage.removeItem("pendingEnrollment");
                navigate(`/course/${courseId}/learn`);
              } else {
                showError(
                  "Payment verification pending. Please check your email for confirmation.",
                );
              }
            }
          } else if (statusRes.data?.status === "failed") {
            showError("Payment failed. Please try again.");
            sessionStorage.removeItem("pendingEnrollment");
          } else {
            // Still processing, show waiting message
            showInfo("Payment processing. Please wait...");
          }
        } catch (err) {
          console.error("Payment status check failed:", err);
        }
      }
    };

    checkPendingPayment();
  }, [navigate]);

  // Fetch course data
  useEffect(() => {
    const fetchCourse = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await coursesApi.getCourseById(id);
        if (response.success && response.data) {
          const courseData = response.data;
          let courseModules: any[] = [];
          try {
            const modulesRes = await modulesApi.getCourseModules(id);
            if (modulesRes.success && modulesRes.data) {
              courseModules = modulesRes.data.map((mod: any) => ({
                id: mod._id,
                title: mod.title,
                description: mod.description,
                order: mod.order,
                isPublished: mod.isPublished,
                lessons:
                  mod.lessons?.map((les: any) => ({
                    id: les._id || les.id,
                    title: les.title,
                    type: les.type,
                    duration: les.duration || 0,
                    isPreview: les.isPreview,
                    videoUrl: les.videoUrl,
                    content: les.content,
                    bullets: les.bullets,
                    topics: les.topics,
                  })) || [],
                totalDuration: mod.totalDuration || 0,
                totalLessons: mod.lessons?.length || 0,
                quiz: mod.quiz || null,
                hasQuiz: mod.hasQuiz || false,
              }));
            }
          } catch (modErr) {
            console.error("Failed to fetch modules:", modErr);
          }

          const mappedCourse: Course = {
            id: courseData._id,
            title: courseData.title,
            description: courseData.description,
            shortDescription: courseData.shortDescription,
            category: courseData.category,
            difficulty: courseData.difficulty,
            language: courseData.language,
            thumbnail: courseData.thumbnail || courseData.thumbnailUrl || "",
            promoVideo: courseData.promoVideo,
            price: courseData.price,
            originalPrice: courseData.originalPrice || courseData.price,
            currency: courseData.currency || "XAF",
            discount: courseData.discount || 0,
            instructorId:
              typeof courseData.instructor === "object"
                ? courseData.instructor._id
                : "",
            instructorName:
              typeof courseData.instructor === "object"
                ? `${courseData.instructor.firstName || ""} ${courseData.instructor.lastName || ""}`.trim()
                : courseData.instructorName || "",
            instructorTitle:
              typeof courseData.instructor === "object"
                ? courseData.instructor.title || "Instructor"
                : "Instructor",
            instructorAvatar:
              typeof courseData.instructor === "object"
                ? courseData.instructor.avatar
                : courseData.instructorAvatar,
            instructorBio:
              typeof courseData.instructor === "object"
                ? courseData.instructor.bio
                : courseData.instructorBio,
            instructorRating:
              typeof courseData.instructor === "object"
                ? courseData.instructor.rating || 0
                : courseData.instructorRating || 0,
            instructorStudents:
              typeof courseData.instructor === "object"
                ? courseData.instructor.studentsCount || 0
                : courseData.instructorStudents || 0,
            instructorCourses:
              typeof courseData.instructor === "object"
                ? courseData.instructor.coursesCount || 0
                : courseData.instructorCourses || 0,
            modules: courseModules,
            totalDuration:
              courseData.totalDuration ||
              courseModules.reduce(
                (sum: number, m: any) => sum + (m.totalDuration || 0),
                0,
              ),
            totalLessons:
              courseData.totalLessons ||
              courseModules.reduce(
                (sum: number, m: any) => sum + (m.lessons?.length || 0),
                0,
              ),
            enrolledStudents: courseData.enrolledStudents || 0,
            rating: courseData.rating || 0,
            reviewsCount: courseData.reviewsCount || 0,
            lastUpdated: courseData.updatedAt
              ? new Date(courseData.updatedAt).toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })
              : "",
            published: courseData.isPublished,
            whatYouWillLearn: courseData.whatYouWillLearn,
            requirements: courseData.requirements,
            prerequisites: courseData.prerequisites || "",
            reviews: [],
            tags: courseData.tags || [],
            certificate: courseData.certificate ?? true,
            downloadableResources: courseData.downloadableResources || 0,
            articlesCount: courseData.articlesCount || 0,
            mobileAccess: courseData.mobileAccess ?? true,
          };

          setCourse(mappedCourse);

          if (courseModules.length > 0) {
            setOpenModules(new Set([courseModules[0].id]));
            if (courseModules[0].lessons.length > 0) {
              setSelectedLesson(courseModules[0].lessons[0]);
            }
          }

          try {
            const reviewsRes = await reviewsApi.getCourseReviews(id, {
              limit: 10,
            });
            if (reviewsRes.success && reviewsRes.data) {
              const fetchedReviews = reviewsRes.data.map((r: any) => ({
                id: r._id,
                userName:
                  r.user?.firstName && r.user?.lastName
                    ? `${r.user.firstName} ${r.user.lastName}`.trim()
                    : r.user?.firstName || r.user?.name || "Anonymous",
                avatar: r.user?.avatar || "https://via.placeholder.com/150",
                rating: r.rating,
                date: new Date(r.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                }),
                comment: r.comment,
                helpful: r.helpful || 0,
              }));
              setCourse((prev) =>
                prev ? { ...prev, reviews: fetchedReviews } : null,
              );
            }
          } catch (revErr) {
            console.error("Failed to fetch reviews:", revErr);
          }
        }
      } catch (err) {
        console.error("Failed to load course:", err);
        showError("Failed to load course details");
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [id]);

  // Check enrollment status
  useEffect(() => {
    const checkEnrollment = async () => {
      if (!id || !isAuthenticated) {
        setCheckingEnrollment(false);
        return;
      }
      try {
        const response = await enrollmentApi.checkEnrollmentStatus(id);
        setIsEnrolled(response.isEnrolled);
      } catch (error: any) {
        setIsEnrolled(false);
      } finally {
        setCheckingEnrollment(false);
      }
    };
    checkEnrollment();
  }, [id, isAuthenticated]);

  // Check wishlist status
  useEffect(() => {
    const checkWishlist = async () => {
      if (!id || !isAuthenticated) return;
      try {
        const response = await wishlistApi.isInWishlist(id);
        if (response.success && response.data !== undefined) {
          setIsWishlisted(response.data);
        }
      } catch (error) {
        console.log("Failed to check wishlist status:", error);
      }
    };
    checkWishlist();
  }, [id, isAuthenticated]);

  const toggleModule = (mid: string) =>
    setOpenModules((prev) => {
      const n = new Set(prev);
      n.has(mid) ? n.delete(mid) : n.add(mid);
      return n;
    });

  const handleEnroll = () => {
    if (!id) return;
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/course/${id}` } });
      return;
    }
    if (course && course.price <= 0) {
      handleFreeEnrollment();
    } else {
      handlePaidEnrollment();
    }
  };

  const handleFreeEnrollment = async () => {
    if (!id) return;
    setEnrollmentLoading(true);
    try {
      const response = await enrollmentApi.enrollInCourse(id);
      if (response.success) {
        setIsEnrolled(true);
        showSuccess("Successfully enrolled!");
        navigate(`/course/${id}/learn`);
      } else {
        throw new Error(response.message || "Failed to enroll");
      }
    } catch (err: any) {
      showError(err.message || "Failed to enroll");
    } finally {
      setEnrollmentLoading(false);
    }
  };

  const handlePaidEnrollment = async () => {
    if (!id) return;
    setEnrollmentLoading(true);
    try {
      const paymentResponse = await paymentApi.createPayment(
        id,
        undefined,
        discountAmount,
      );
      if (paymentResponse.success && paymentResponse.data?.paymentLink) {
        sessionStorage.setItem(
          "pendingEnrollment",
          JSON.stringify({
            courseId: id,
            transactionId: paymentResponse.data.transactionId,
            amount: paymentResponse.data.amount,
          }),
        );
        window.location.href = paymentResponse.data.paymentLink;
      } else {
        throw new Error(paymentResponse.message || "Failed to create payment");
      }
    } catch (err: any) {
      console.error("Payment error:", err);
      showError(err.message || "Failed to initiate payment");
      setEnrollmentLoading(false);
    }
  };

  const handleToggleWishlist = async () => {
    if (!id || !isAuthenticated) {
      navigate("/login", { state: { from: `/course/${id}` } });
      return;
    }
    setWishlistLoading(true);
    try {
      if (isWishlisted) {
        await wishlistApi.removeFromWishlist(id);
        setIsWishlisted(false);
        showSuccess("Removed from wishlist");
      } else {
        await wishlistApi.addToWishlist(id);
        setIsWishlisted(true);
        showSuccess("Added to wishlist");
      }
    } catch (err: any) {
      showError(err.message || "Failed to update wishlist");
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!id || !newReviewComment.trim()) {
      showError("Please write a review");
      return;
    }
    setSubmittingReview(true);
    try {
      const response = await reviewsApi.createReview(id, {
        rating: newReviewRating,
        comment: newReviewComment,
      });
      if (response.success) {
        showSuccess("Review submitted!");
        setShowReviewForm(false);
        setNewReviewRating(5);
        setNewReviewComment("");
        const reviewsRes = await reviewsApi.getCourseReviews(id, { limit: 10 });
        if (reviewsRes.success && reviewsRes.data) {
          const fetchedReviews = reviewsRes.data.map((r: any) => ({
            id: r._id,
            userName: r.user?.firstName + " " + r.user?.lastName || "Anonymous",
            avatar: r.user?.avatar || "https://via.placeholder.com/150",
            rating: r.rating,
            date: new Date(r.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
            comment: r.comment,
            helpful: r.helpful || 0,
          }));
          setCourse((prev) =>
            prev ? { ...prev, reviews: fetchedReviews } : null,
          );
        }
      }
    } catch (err: any) {
      showError(err.message || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleMarkHelpful = async (reviewId: string) => {
    if (helpfulReviews.has(reviewId)) return;
    try {
      await reviewsApi.markHelpful(reviewId);
      setHelpfulReviews((prev) => new Set(prev).add(reviewId));
    } catch (err) {
      console.error("Failed to mark helpful:", err);
    }
  };

  const getPreviewVideo = () => {
    if (!course?.modules) return null;
    for (const module of course.modules) {
      for (const lesson of module.lessons) {
        if (lesson.isPreview && lesson.videoUrl) {
          // Format YouTube URL for embedding
          let videoUrl = lesson.videoUrl;
          // Convert youtube.com/watch?v= to youtube.com/embed/
          if (videoUrl.includes("youtube.com/watch")) {
            const videoId = videoUrl.split("v=")[1]?.split("&")[0];
            if (videoId) {
              return `https://www.youtube.com/embed/${videoId}`;
            }
          }
          // Convert youtu.be/ to youtube.com/embed/
          if (videoUrl.includes("youtu.be/")) {
            const videoId = videoUrl.split("youtu.be/")[1]?.split("?")[0];
            if (videoId) {
              return `https://www.youtube.com/embed/${videoId}`;
            }
          }
          return videoUrl;
        }
      }
    }
    return null;
  };

  const previewVideoUrl = getPreviewVideo();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="space-y-3 text-center">
          <div className="w-10 h-10 mx-auto border-2 border-orange-500 rounded-full border-t-transparent animate-spin" />
          <p className="text-xs text-gray-400 uppercase tracking-wider">
            Loading course...
          </p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Course Not Found
          </h2>
          <Link
            to="/courses"
            className="text-sm text-orange-600 hover:underline"
          >
            Browse courses
          </Link>
        </div>
      </div>
    );
  }

  const visibleModules = showAllModules
    ? course.modules
    : course.modules.slice(0, 4);
  const totalModuleLessons = course.modules.reduce(
    (s, m) => s + m.lessons.length,
    0,
  );
  const progress = 0;

  return (
    <>
      {/* Hero Section - Modern Coursera Style */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white mt-4 py-7">
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Left Column - Content */}
            <div className="md:col-span-2">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">
                  {course.category}
                </span>
                <DifficultyBadge level={course.difficulty} />
                {course.certificate && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    Certificate
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                {course.title}
              </h1>

              <p className="text-gray-300 text-sm md:text-base mb-4 line-clamp-3">
                {course.description}
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-lg font-bold text-amber-400">
                    {course.rating}
                  </span>
                  <RatingStars rating={course.rating} />
                  <span className="text-gray-400 ml-1">
                    ({course.reviewsCount.toLocaleString()} ratings)
                  </span>
                </div>
                <div className="flex items-center gap-1 text-gray-300">
                  <Users className="w-4 h-4" />
                  <span>
                    {course.enrolledStudents.toLocaleString()} students
                  </span>
                </div>
                <div className="flex items-center gap-1 text-gray-300">
                  <Clock className="w-4 h-4" />
                  <span>{formatDuration(course.totalDuration)} total</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <img
                  src={course.instructorAvatar}
                  alt={course.instructorName}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-xs text-gray-400">Created by</p>
                  <p className="text-sm font-medium text-white">
                    {course.instructorName}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Video Preview */}
            <div className="relative">
              <div className="bg-black rounded-xl overflow-hidden shadow-2xl">
                {previewVideoUrl ? (
                  <div className="relative aspect-video">
                    <iframe
                      src={previewVideoUrl}
                      title="Course Preview"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      onError={(e) => {
                        console.error("Video failed to load:", previewVideoUrl);
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ) : course?.thumbnail ? (
                  <div className="relative aspect-video">
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="text-center">
                        <PlayCircle className="w-16 h-16 text-white/80 mx-auto mb-2" />
                        <p className="text-white text-sm">
                          Preview not available
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-800 flex items-center justify-center">
                    <div className="text-center">
                      <PlayCircle className="w-16 h-16 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm">
                        Course preview coming soon
                      </p>
                    </div>
                  </div>
                )}
                <div className="absolute top-3 left-3 bg-black/70 text-white text-xs font-medium px-2 py-1 rounded-md">
                  Preview
                </div>
              </div>

              {/* Enroll Card */}
              <div className="mt-4 bg-white rounded-xl shadow-lg p-4 border border-gray-200">
                {course.price === 0 ? (
                  <div className="mb-3">
                    <span className="text-2xl font-bold text-green-600">
                      FREE
                    </span>
                  </div>
                ) : (
                  <div className="mb-3">
                    <span className="text-2xl font-bold text-gray-900">
                      {fmtPrice(course.price, course.currency)}
                    </span>
                    <span className="text-sm text-gray-400 line-through ml-2">
                      {fmtPrice(course.originalPrice, course.currency)}
                    </span>
                    <span className="ml-2 text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                      Save {course.discount}%
                    </span>
                  </div>
                )}

                {!isAuthenticated ? (
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    Login to Enroll
                  </button>
                ) : !isEnrolled ? (
                  <button
                    onClick={handleEnroll}
                    disabled={enrollmentLoading}
                    className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                  >
                    {enrollmentLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </div>
                    ) : course.price === 0 ? (
                      "Enroll for Free"
                    ) : (
                      `Enroll Now - ${fmtPrice(course.price, course.currency)}`
                    )}
                  </button>
                ) : (
                  <Link
                    to={`/course/${course.id}/learn`}
                    className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <PlayCircle className="w-4 h-4" /> Continue Learning
                  </Link>
                )}

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleToggleWishlist}
                    disabled={wishlistLoading}
                    className={`flex-1 py-2 border rounded-lg text-sm font-medium flex items-center justify-center gap-1 transition-colors
          ${isWishlisted ? "border-rose-300 text-rose-600 bg-rose-50" : "border-gray-300 text-gray-700 hover:border-gray-400"}`}
                  >
                    <Heart
                      className={`w-4 h-4 ${isWishlisted ? "fill-rose-500 text-rose-500" : ""}`}
                    />
                    {isWishlisted ? "Wishlisted" : "Wishlist"}
                  </button>
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 flex items-center justify-center gap-1 hover:border-gray-400 transition-colors"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>

                {/* <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 text-center">
                    30-day money-back guarantee
                  </p>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex gap-6 overflow-x-auto">
            {["overview", "curriculum", "instructor", "reviews"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`py-3 text-sm font-semibold capitalize transition-colors border-b-2 whitespace-nowrap
                  ${activeTab === tab ? "border-orange-600 text-orange-600" : "border-transparent text-gray-600 hover:text-gray-900"}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <>
                {/* What You'll Learn */}
                <div className="border border-gray-200 rounded-xl p-5">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-500" />
                    What you'll learn
                  </h2>
                  <div className="grid md:grid-cols-2 gap-3">
                    {toArray(course.whatYouWillLearn)
                      .slice(0, 8)
                      .map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                          <span className="text-sm text-gray-700">{item}</span>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Description */}
                <div className="border border-gray-200 rounded-xl p-5">
                  <h2 className="text-lg font-bold text-gray-900 mb-3">
                    Description
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    {course.description}
                  </p>
                </div>

                {/* Requirements */}
                <div className="border border-gray-200 rounded-xl p-5">
                  <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    Requirements
                  </h2>
                  <ul className="space-y-2">
                    {toArray(course.requirements)
                      .slice(0, 5)
                      .map((req, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-gray-600"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2" />
                          <span>{req}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              </>
            )}

            {/* Curriculum Tab */}
            {activeTab === "curriculum" && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-5 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-lg font-bold text-gray-900">
                    Course Curriculum
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {course.modules.length} sections • {totalModuleLessons}{" "}
                    lessons • {formatDuration(course.totalDuration)} total
                  </p>
                </div>
                <div className="divide-y divide-gray-200">
                  {visibleModules.map((module, idx) => {
                    const isOpen = openModules.has(module.id);
                    return (
                      <div key={module.id}>
                        <button
                          onClick={() => toggleModule(module.id)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-bold">
                              {idx + 1}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {module.title}
                              </h3>
                              <p className="text-xs text-gray-500">
                                {module.lessons.length} lessons
                              </p>
                            </div>
                          </div>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>

                        {isOpen && (
                          <div className="bg-gray-50/50 divide-y divide-gray-100">
                            {module.lessons.map((lesson, lIdx) => (
                              <div
                                key={lesson.id}
                                className={`flex items-center gap-3 p-3 pl-14 transition-colors ${selectedLesson?.id === lesson.id ? "bg-orange-50" : "hover:bg-gray-100"}`}
                                onClick={() => {
                                  if (!isEnrolled && !lesson.isPreview) return;
                                  setSelectedLesson(lesson);
                                  setActiveTab("overview");
                                }}
                              >
                                <LessonIcon type={lesson.type} />
                                <div className="flex-1">
                                  <p className="text-sm text-gray-900">
                                    {lesson.title}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatDuration(lesson.duration)}
                                  </p>
                                </div>
                                {lesson.isPreview && (
                                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                    Preview
                                  </span>
                                )}
                                {!isEnrolled && !lesson.isPreview && (
                                  <Lock className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!showAllModules && course.modules.length > 4 && (
                  <button
                    onClick={() => setShowAllModules(true)}
                    className="w-full p-3 text-center text-orange-600 font-medium hover:bg-orange-50 transition-colors"
                  >
                    Show {course.modules.length - 4} more sections
                  </button>
                )}
              </div>
            )}

            {/* Instructor Tab */}
            {activeTab === "instructor" && (
              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={course.instructorAvatar}
                    alt={course.instructorName}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      {course.instructorName}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {course.instructorTitle}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-sm">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />{" "}
                        {course.instructorRating}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />{" "}
                        {course.instructorStudents.toLocaleString()} students
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {course.instructorBio ||
                    "Experienced instructor passionate about teaching and helping students succeed."}
                </p>
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === "reviews" && (
              <div className="space-y-5">
                {/* Rating Summary */}
                <div className="border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-gray-900">
                        {course.rating}
                      </div>
                      <RatingStars rating={course.rating} size="md" />
                      <div className="text-sm text-gray-500 mt-1">
                        Course rating
                      </div>
                    </div>
                    <div className="flex-1 space-y-1">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const percentage =
                          star === 5
                            ? 72
                            : star === 4
                              ? 20
                              : star === 3
                                ? 6
                                : star === 2
                                  ? 1
                                  : 1;
                        return (
                          <div key={star} className="flex items-center gap-2">
                            <span className="text-sm w-6">{star}</span>
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-amber-400 rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-500 w-8">
                              {percentage}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Write Review Button */}
                {isEnrolled && (
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700"
                  >
                    Write a Review
                  </button>
                )}

                {/* Review Form */}
                {showReviewForm && (
                  <div className="border border-gray-200 rounded-xl p-5">
                    <h3 className="font-bold mb-3">Write Your Review</h3>
                    <div className="space-y-3">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setNewReviewRating(star)}
                          >
                            <Star
                              className={`w-6 h-6 ${newReviewRating >= star ? "text-amber-400 fill-amber-400" : "text-gray-300"}`}
                            />
                          </button>
                        ))}
                      </div>
                      <textarea
                        rows={3}
                        value={newReviewComment}
                        onChange={(e) => setNewReviewComment(e.target.value)}
                        placeholder="Share your experience..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSubmitReview}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg"
                        >
                          Submit
                        </button>
                        <button
                          onClick={() => setShowReviewForm(false)}
                          className="px-4 py-2 border rounded-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reviews List */}
                <div className="space-y-4">
                  {course.reviews.slice(0, 5).map((review) => (
                    <div
                      key={review.id}
                      className="border-b border-gray-100 pb-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <img
                          src={review.avatar}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-medium text-gray-900">
                            {review.userName}
                          </p>
                          <div className="flex items-center gap-2">
                            <RatingStars rating={review.rating} />
                            <span className="text-xs text-gray-400">
                              {review.date}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm">{review.comment}</p>
                      <button
                        onClick={() => handleMarkHelpful(review.id)}
                        className="mt-2 text-xs text-gray-500 hover:text-orange-600"
                      >
                        <ThumbsUp className="w-3 h-3 inline mr-1" /> Helpful (
                        {review.helpful})
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Course Content */}
          <div className="hidden lg:block">
            <div className="sticky top-24 border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h3 className="font-bold text-gray-900">Course Content</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {course.modules.length} sections • {course.totalLessons}{" "}
                  lessons
                </p>
              </div>
              <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100">
                {course.modules.slice(0, 6).map((module, idx) => (
                  <div key={module.id}>
                    <div className="p-3 bg-white">
                      <p className="text-sm font-medium text-gray-900">
                        {idx + 1}. {truncateText(module.title, 40)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {module.lessons.length} lessons
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={course.title}
        thumbnail={course.thumbnail}
        url={`${window.location.origin}/course/${id}`}
      />
    </>
  );
};

export default CourseDetailPage;
